import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import moment from "moment";
import CustomTextField from "../../common/CustomTextField";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import SignaturePad, { SignatureData } from "../../common/signature/SignaturePad";
import { useModalStyles } from "../../common/useModalStyles";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { previewGenesisCardAuthorization } from "../../../redux/slice/prescription/prescriptionSlice";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";

/**
 * Genesis Compounding "Credit Card Authorization Form".
 *
 * Field names are deliberately neutral (acct*) instead of card/cvv: Chrome
 * classifies a card-named group as a payment method and, on plain HTTP, warns
 * that "automatic payment methods filling is disabled".
 */
export type GenesisBillingFormValues = {
    acctHolder: string;
    acctPan: string;
    acctThru: string;
    acctCheck: string;
    billingAddress: string;
    authHolder: string;
    signature: string;
    signatureDate: string;
};

const SIGNATURE_DATE_FORMAT = "MM/DD/YYYY";
const EXPIRY_FORMAT = "MM/YY";

interface GenesisBillingFormPopupProps {
    open: boolean;
    onClose: () => void;
    pharmacyDisplayName: string;
    /** Billing address is the only field V3 already knows; card data is never prefilled. */
    prefill?: { billingAddress?: string };
}

const emptyValues: GenesisBillingFormValues = {
    acctHolder: "",
    acctPan: "",
    acctThru: "",
    acctCheck: "",
    billingAddress: "",
    authHolder: "",
    signature: "",
    signatureDate: "",
};

function digitsOnly(value: string | undefined): string {
    return (value || "").replace(/\D/g, "");
}

function validateCardNumber(value: string | undefined): true | string {
    const n = digitsOnly(value).length;
    return n >= 13 && n <= 19 ? true : "Enter a valid card number.";
}

function validateCvv(value: string | undefined): true | string {
    const n = digitsOnly(value).length;
    return n >= 3 && n <= 4 ? true : "Enter a valid CVC/CVV.";
}

function validateExpiry(value: string | undefined): true | string {
    const expiry = moment(value, EXPIRY_FORMAT, true);
    if (!expiry.isValid()) return "Use MM/YY format.";
    if (expiry.endOf("month").isBefore(moment(), "day")) return "Card has expired.";
    return true;
}

function validateSignatureDate(value: string | undefined): true | string {
    const signed = moment(value, SIGNATURE_DATE_FORMAT, true);
    if (!signed.isValid()) return "Use MM/DD/YYYY format.";
    if (signed.isAfter(moment(), "day")) return "Date cannot be in the future.";
    return true;
}

const GenesisBillingFormPopup: React.FC<GenesisBillingFormPopupProps> = ({
    open,
    onClose,
    pharmacyDisplayName,
    prefill,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { tokens, isDark, paperSx, primaryBtnSx, cancelBtnSx, inputFieldSx } = useModalStyles();
    const dispatch = useAppDispatch();
    const [previewBase64, setPreviewBase64] = React.useState("");
    const [isPreviewing, setIsPreviewing] = React.useState(false);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<GenesisBillingFormValues>({ defaultValues: emptyValues });
    const cardholderName = watch("acctHolder");

    React.useEffect(() => {
        if (!open) return;
        reset({
            ...emptyValues,
            billingAddress: prefill?.billingAddress ?? "",
            signatureDate: moment().format(SIGNATURE_DATE_FORMAT),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /* The values are posted once so the pharmacy's PDF template can be filled and
       flattened server-side; nothing is stored, and the response is the PDF itself. */
    const onValid = async (values: GenesisBillingFormValues) => {
        setIsPreviewing(true);
        try {
            const document = await dispatch(
                previewGenesisCardAuthorization({
                    request: {
                        cardholderName: values.acctHolder,
                        cardNumber: values.acctPan,
                        expirationDate: values.acctThru,
                        cvv: values.acctCheck,
                        billingAddress: values.billingAddress,
                        signature: values.signature,
                        signedDate: values.signatureDate,
                    },
                }),
            ).unwrap();
            const content = document?.content;
            if (!content) throw new Error("The authorization form came back empty.");
            setPreviewBase64(content);
        } catch (error: any) {
            dispatch(setSingleAlertObj({
                message: typeof error === "string"
                    ? error
                    : error?.message || "Unable to generate the authorization form.",
                alertType: "error",
            }));
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleSignatureChange = (data: SignatureData) => {
        setValue("signature", data.signature, { shouldValidate: true });
        setValue("authHolder", data.typedText, { shouldValidate: true });
    };

    const noAutoFill = { autoComplete: "off", autoCorrect: "off", spellCheck: false };

    const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
        <Typography
            sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: tokens.textSecondary,
                mb: 0.6,
                display: "flex",
                alignItems: "center",
                gap: 0.3,
            }}
        >
            {label}
            {required && (
                <Box component="span" sx={{ color: tokens.accent }}>
                    *
                </Box>
            )}
        </Typography>
    );

    const SectionTitle = ({ label }: { label: string }) => (
        <Box sx={{ mt: 0.5 }}>
            <Divider sx={{ borderColor: tokens.border }}>
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        color: tokens.textSecondary,
                        px: 1,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                    }}
                >
                    {label}
                </Typography>
            </Divider>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{ sx: paperSx(isMobile) }}
            TransitionProps={{
                onExited: () => {
                    reset(emptyValues);
                    setPreviewBase64("");
                },
            }}
        >
            {/*
              Not a <form>: Chrome treats card-like fields inside a form on HTTP as a
              payment method and shows "Automatic payment methods filling is disabled...".
            */}
            <Box
                component="div"
                onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    const tag = (e.target as HTMLElement).tagName;
                    if (tag === "BUTTON" || tag === "TEXTAREA") return;
                    e.preventDefault();
                    void handleSubmit(onValid)();
                }}
                sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
            >
                {/* ── Header ── */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 3,
                        py: 2,
                        borderBottom: `1px solid ${tokens.border}`,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            flexShrink: 0,
                            backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.12)",
                            border: "1.5px solid rgba(20,184,166,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CreditCardOutlinedIcon sx={{ fontSize: "1.2rem", color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            fontWeight={700}
                            sx={{ fontSize: "1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
                        >
                            Credit Card Authorization Form
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.1 }}>
                            {pharmacyDisplayName}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        disableRipple
                        sx={{
                            width: 28, height: 28, borderRadius: "7px",
                            color: tokens.textSecondary,
                            "&:hover": {
                                backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                color: tokens.textPrimary,
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                </Box>

                {/* ── Body ── */}
                <DialogContent sx={{ px: 3, py: 2.5, overflowY: "auto", flex: 1, minHeight: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "0.8rem",
                            color: tokens.textPrimary,
                            lineHeight: 1.7,
                            p: 1.5,
                            borderRadius: "10px",
                            border: `1px solid ${tokens.border}`,
                            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : tokens.inputBg,
                        }}
                    >
                        {`Please complete all fields. This authorization may be canceled at any time by contacting ${pharmacyDisplayName}. Unless canceled, this authorization will remain in effect.`}
                    </Typography>

                    <Box sx={{ mt: 2.5 }}>
                        <SectionTitle label="Credit Card Information" />
                    </Box>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FieldLabel label="Cardholder Name (as shown on card)" required />
                            <CustomTextField
                                control={control}
                                controllerName="acctHolder"
                                name="acctHolder"
                                label=""
                                size="small"
                                placeholder="Name as shown on card"
                                inputProps={noAutoFill}
                                rules={{ required: "Cardholder Name is required." }}
                                sx={inputFieldSx(!!errors?.acctHolder)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FieldLabel label="Card Number" required />
                            <CustomTextField
                                control={control}
                                controllerName="acctPan"
                                name="acctPan"
                                label=""
                                size="small"
                                placeholder="Enter number"
                                isNumberOnly
                                maxLength={19}
                                inputProps={noAutoFill}
                                rules={{ required: "Card Number is required.", validate: validateCardNumber }}
                                sx={inputFieldSx(!!errors?.acctPan)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FieldLabel label="Expiration Date (MM/YY)" required />
                            <Box sx={inputFieldSx(!!errors?.acctThru)}>
                                <CustomPatternTextField
                                    control={control}
                                    name="acctThru"
                                    mask="99/99"
                                    label=""
                                    size="small"
                                    placeholder="MM/YY"
                                    autoComplete="off"
                                    rules={{ required: "Expiration Date is required.", validate: validateExpiry }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FieldLabel label="CVC/CVV" required />
                            <CustomTextField
                                control={control}
                                controllerName="acctCheck"
                                name="acctCheck"
                                label=""
                                size="small"
                                placeholder="Code"
                                isNumberOnly
                                maxLength={4}
                                inputProps={noAutoFill}
                                rules={{ required: "CVC/CVV is required.", validate: validateCvv }}
                                sx={inputFieldSx(!!errors?.acctCheck)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FieldLabel label="Billing Address" required />
                            <CustomTextField
                                control={control}
                                controllerName="billingAddress"
                                label=""
                                size="small"
                                placeholder="Street, city, state, ZIP"
                                multiline
                                minRows={2}
                                rules={{ required: "Billing Address is required." }}
                                sx={inputFieldSx(!!errors?.billingAddress)}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2.5 }}>
                        <SectionTitle label="Authorization" />
                    </Box>
                    <Typography
                        sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 1.5, lineHeight: 1.7 }}
                    >
                        {`I authorize ${pharmacyDisplayName} to charge the credit card listed above for agreed-upon purchases. I understand that my credit card information will be securely stored on file and may be used for future transactions associated with my account unless this authorization is revoked.`}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <SignaturePad
                                onSignatureChange={handleSignatureChange}
                                name="signature"
                                textName="authHolder"
                                control={control}
                                validationRules={{
                                    name: { required: "Cardholder Name is required." },
                                    signature: { required: "Cardholder Signature is required." },
                                }}
                                setSignOutside={cardholderName}
                                signature=""
                                disabled={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FieldLabel label="Date" required />
                            <Box sx={inputFieldSx(!!errors?.signatureDate)}>
                                <CustomPatternTextField
                                    control={control}
                                    name="signatureDate"
                                    mask="99/99/9999"
                                    label=""
                                    size="small"
                                    placeholder="MM/DD/YYYY"
                                    autoComplete="off"
                                    rules={{
                                        required: "Date is required.",
                                        validate: validateSignatureDate,
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* ── Footer ── */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${tokens.border}`,
                    }}
                >
                    <Button
                        type="button"
                        variant="contained"
                        onClick={handleSubmit(onValid)}
                        disabled={isPreviewing}
                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
                        sx={primaryBtnSx}
                    >
                        Preview
                    </Button>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                        sx={cancelBtnSx}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>

            {!!previewBase64 && (
                <PDFViewer
                    isOpen
                    base64String={previewBase64}
                    title="Credit Card Authorization Form"
                    fileName="credit_card_authorization_form.pdf"
                    onClose={() => setPreviewBase64("")}
                />
            )}
        </Dialog>
    );
};

export default GenesisBillingFormPopup;
